import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import { getFormattedPayload, mapTooltipColors } from './customTooltip'
import { VuiPopOver, VuiChartTooltip } from '@topkamera/viso-ui'
import { formatDate } from './utils'

const ChartTooltip = ({
  chartContentRef,
  popOverElementId = 'popoverRoot',
  rechartsProps,
  title,
  chartDef,
  dashboardProps = {}
}) => {
  const { coordinate, label, payload, active } = rechartsProps
  const { chartConfig = {}, chartData = {}, uniqueGroupByValue } = dashboardProps
  const anchorRef = useRef(null)
  const [anchorPos, setAnchorPos] = useState(null)
  const isOpen = Boolean(active && payload && payload.length > 0 && coordinate)

  const mapTooltipItems = () => {
    const { format, decimal } = chartConfig
    const overrides = chartConfig.yDataOverrides || {}
    const groups = chartData.groupBy
    const groupByColors = isEmpty(uniqueGroupByValue) ? chartConfig.yDataColorObj : uniqueGroupByValue
    const mappedColors = mapTooltipColors(chartData, chartConfig)
    const hasMappedColors = mappedColors.length > 0
    let mapTooltip = []

    if (active && payload?.length) {
      const group = payload[0]?.payload?.group || {}
      const mapPayload = getFormattedPayload(payload, format, overrides, decimal, hasMappedColors ? groupByColors : '', chartConfig) || []

      // Format label using the same date formatter as X-axis
      let formattedLabel = label
      if (label && chartConfig?.groupBy?.metrics?.some(m => /time/i.test(m))) {
        formattedLabel = formatDate(label, chartConfig.chartDateFormat)
      }

      mapTooltip.push({
        hasDivider: true,
        label: chartDef?.tooltipTitle || title || 'Date',
        value: formattedLabel ? formattedLabel?.toString() : isEmpty(groups) ?
          Object.entries(group).map(([key, value]) =>
            <span key={key}>{value}</span>
          ) : groups.map(key =>
            <span key={key}>{group[key]}</span>
          )
      })

      mapPayload.forEach((metric, index) => {
        let color = hasMappedColors ? mappedColors[index]?.color : metric.color

        if (chartDef) {
          const findMetric = chartDef?.metrics.find(def => def.dataKey === metric.name)
          color = findMetric?.fill
        }
        mapTooltip.push({
          hasDivider: false,
          label: metric.name,
          value: metric.value,
          color: color
        })
      })
      return mapTooltip
    }
    return []
  }

  useEffect(() => {
    if (!isOpen) {
      setAnchorPos(null)
      return
    }

    if (!coordinate) {
      setAnchorPos(null)
      return
    }

    setAnchorPos({
      left: coordinate.x,
      top: coordinate.y,
    })
  }, [chartContentRef, coordinate, isOpen])

  const rows = useMemo(() => {
    if (!payload) return []
    return mapTooltipItems()
  }, [payload])

  if (!isOpen) return null

  return (
    <>
      <div
        ref={anchorRef}
        style={{
          position: 'absolute',
          left: anchorPos ? anchorPos.left : 0,
          top: anchorPos ? anchorPos.top : 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
      />
      <VuiPopOver
        elementId={popOverElementId}
        isOpen={isOpen}
        onClose={() => { }}
        anchorRef={anchorRef}
        position='right'
        alignment='start'
        animation='fade'
        offset={8}
        noStyling
      >
        <VuiChartTooltip payload={rows} />
      </VuiPopOver>
    </>
  )
}

export default ChartTooltip